import { useState } from "react";
import {
  addCommasToNumberString,
  removeCommasFromNumberString,
} from "components/monetary-input/definitions";
import { BreakdownState, BreakdownHandlers } from "./definitions";

export function calculateAssessmentTotal(amounts: {
  tax: string;
  penalty: string;
  interest: string;
  other: string;
}): string {
  const tax = parseAmountForCalculation(amounts.tax);
  const penalty = parseAmountForCalculation(amounts.penalty);
  const interest = parseAmountForCalculation(amounts.interest);
  const other = parseAmountForCalculation(amounts.other);

  const totalVal = tax + penalty + interest + other;
  return totalVal === 0 ? "" : formatAmountForDisplay(totalVal);
}

export function formatAmountForDisplay(amount: number): string {
  return addCommasToNumberString(amount.toFixed(2));
}

export function parseAmountForCalculation(amount: string): number {
  return Number(removeCommasFromNumberString(amount)) || 0;
}

export function createBreakdownHandlers(
  state: BreakdownState,
  setState: React.Dispatch<React.SetStateAction<BreakdownState>>
): BreakdownHandlers {
  return {
    handleTaxYearChange: (checked: boolean) => {
      setState((prev) => ({
        ...prev,
        byTaxYear: checked,
        byTaxYearArea: checked ? false : prev.byTaxYearArea,
      }));
    },

    handleTaxAreaChange: (checked: boolean) => {
      setState((prev) => ({
        ...prev,
        byTaxArea: checked,
        byTaxYearArea: checked ? false : prev.byTaxYearArea,
      }));
    },

    handleTaxYearAreaChange: (checked: boolean) => {
      setState((prev) => ({
        ...prev,
        byTaxYearArea: checked,
        byTaxYear: checked ? false : prev.byTaxYear,
        byTaxArea: checked ? false : prev.byTaxArea,
      }));
    },
  };
}

export function getBreakdownDisabledStates(
  state: BreakdownState,
  hasTaxAreas: boolean
) {
  return {
    taxYear: state.byTaxYearArea,
    taxArea: !hasTaxAreas || state.byTaxYearArea,
    taxYearArea: !hasTaxAreas || state.byTaxYear || state.byTaxArea,
  };
}

export function useBreakdownState(hasTaxAreas: boolean = false) {
  const [state, setState] = useState<BreakdownState>({
    byTaxYear: false,
    byTaxArea: false,
    byTaxYearArea: false,
  });

  const handlers = createBreakdownHandlers(state, setState);
  const disabledStates = getBreakdownDisabledStates(state, hasTaxAreas);

  return {
    state,
    handlers,
    disabledStates,
  };
}

// Helper function to calculate totals from breakdowns in a single pass
export function calculateBreakdownTotals(breakdowns: any[]): {
  totalTax: number;
  totalPenalty: number;
  totalInterest: number;
  totalOther: number;
} {
  return breakdowns.reduce(
    (totals, breakdown) => ({
      totalTax: totals.totalTax + Number(breakdown.totalTaxAmount || 0),
      totalPenalty:
        totals.totalPenalty + Number(breakdown.totalPenaltyAmount || 0),
      totalInterest:
        totals.totalInterest + Number(breakdown.totalInterestAmount || 0),
      totalOther: totals.totalOther + Number(breakdown.totalOtherAmount || 0),
    }),
    { totalTax: 0, totalPenalty: 0, totalInterest: 0, totalOther: 0 }
  );
}

// Validation functions (Local implementations with core logic)
export function validateTaxYearBreakdownLocal(
  breakdowns: any[],
  targetAmounts: {
    totalTaxAmount: string;
    totalPenaltyAmount: string;
    totalInterestAmount: string;
    totalOtherAmount: string;
  }
): boolean {
  const totals = calculateBreakdownTotals(breakdowns);

  return (
    Math.round(totals.totalTax * 100) / 100 ===
      Number(targetAmounts.totalTaxAmount) &&
    Math.round(totals.totalPenalty * 100) / 100 ===
      Number(targetAmounts.totalPenaltyAmount) &&
    Math.round(totals.totalInterest * 100) / 100 ===
      Number(targetAmounts.totalInterestAmount) &&
    Math.round(totals.totalOther * 100) / 100 ===
      Number(targetAmounts.totalOtherAmount)
  );
}

export function validateTaxAreaBreakdownLocal(
  breakdowns: any[],
  totalTaxAmount: string
): boolean {
  const totalAssessed = breakdowns.reduce(
    (accumulator, currentBreakdown) =>
      accumulator + Number(currentBreakdown.amountAssessed),
    0
  );

  return Math.round(totalAssessed * 100) / 100 === Number(totalTaxAmount);
}

export function validateTaxYearAreaBreakdownLocal(
  breakdowns: any[],
  targetAmounts: {
    totalTaxAmount: string;
    totalPenaltyAmount: string;
    totalInterestAmount: string;
    totalOtherAmount: string;
  },
  onDuplicateError?: () => void,
  onAmountMismatchError?: () => void
): boolean {
  // Check for uniqueness based on Tax Year, Tax Area, Due Date, and Additional Information
  const uniqueCombinations = new Set();
  const duplicateRows: number[] = [];

  breakdowns.forEach((breakdown, index) => {
    const combinationKey = `${breakdown.taxYear || ""}|${
      breakdown.taxAreaId || ""
    }|${breakdown.dueDate || ""}|${breakdown.additionalInformation || ""}`;

    if (uniqueCombinations.has(combinationKey)) {
      duplicateRows.push(index + 1);
    } else {
      uniqueCombinations.add(combinationKey);
    }
  });

  if (duplicateRows.length > 0) {
    if (onDuplicateError) {
      onDuplicateError();
    }
    return false;
  }

  const totals = calculateBreakdownTotals(breakdowns);

  const isValidAmounts =
    Math.round(totals.totalTax * 100) / 100 ===
      Number(targetAmounts.totalTaxAmount) &&
    Math.round(totals.totalPenalty * 100) / 100 ===
      Number(targetAmounts.totalPenaltyAmount) &&
    Math.round(totals.totalInterest * 100) / 100 ===
      Number(targetAmounts.totalInterestAmount) &&
    Math.round(totals.totalOther * 100) / 100 ===
      Number(targetAmounts.totalOtherAmount);

  if (!isValidAmounts && onAmountMismatchError) {
    onAmountMismatchError();
  }

  return isValidAmounts;
}

export function calculateOverviewFromTaxYearArea(breakdowns: any[]): {
  totalTaxAmount: string;
  totalPenaltyAmount: string;
  totalInterestAmount: string;
  totalOtherAmount: string;
} {
  const totals = calculateBreakdownTotals(breakdowns);

  return {
    totalTaxAmount: totals.totalTax.toFixed(2),
    totalPenaltyAmount: totals.totalPenalty.toFixed(2),
    totalInterestAmount: totals.totalInterest.toFixed(2),
    totalOtherAmount: totals.totalOther.toFixed(2),
  };
}

// Form validation
export function validateFormData(data: {
  dateReceived: string;
  type: string;
}): boolean {
  return !!(data.dateReceived && data.type);
}

export function makeUpdateAmount<
  T extends {
    amounts: any;
    dateReceived?: string;
    type?: string;
    notes?: string;
  }
>(formData: T, setFormData: (data: T) => void, control?: any) {
  return (field: string, value: string) => {
    const currentFormValues = control?.getValues ? control.getValues() : {};

    const newData = {
      ...formData,
      // Only override if control has non-empty values to prevent overwriting user input
      dateReceived: currentFormValues.dateReceived || formData.dateReceived,
      type: currentFormValues.type || formData.type,
      notes:
        currentFormValues.notes !== undefined
          ? currentFormValues.notes
          : formData.notes,
      amounts: { ...formData.amounts, [field]: value },
    } as T;
    setFormData(newData);
  };
}
